import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { MapPin, Calendar, TrendingUp, Eye, Heart, Key, Users } from "lucide-react";
import { fmtCLP, resolveImg } from "../lib/api";

export default function BusinessCard({ biz, onFavorite, isFavorite = false, dense = false }) {
  const primary = biz.images?.find((i) => i.is_primary) || biz.images?.[0];
  return (
    <article
      data-testid={`business-card-${biz.slug}`}
      className="bg-white border border-zinc-200 rounded-lg overflow-hidden card-hover flex flex-col"
    >
      <Link to={`/business/${biz.slug}`} className="relative block aspect-[4/3] overflow-hidden bg-zinc-100">
        <img
          src={resolveImg(primary?.storage_path, 500)}
          alt={biz.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105 bg-zinc-100"
          onLoad={(e) => e.currentTarget.classList.add("loaded")}
        />
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white border-0" data-testid={`badge-active-${biz.id}`}>
            Negocio funcionando
          </Badge>
          <Badge className="bg-amber-600 hover:bg-amber-700 text-white border-0 flex items-center gap-1">
            <Key className="w-3 h-3" /> Derecho de llave
          </Badge>
        </div>
        {biz.is_featured && (
          <Badge className="absolute top-3 right-3 bg-slate-900 hover:bg-slate-800 text-amber-400 border-0">
            Destacado
          </Badge>
        )}
        {(biz.interested_count || 0) >= 2 && (
          <div
            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-600/95 text-white text-[11px] font-bold shadow-md backdrop-blur-sm"
            data-testid={`interest-badge-${biz.id}`}
            title="Inversionistas interesados en este negocio"
          >
            <Users className="w-3.5 h-3.5" />
            <span>{biz.interested_count} interesados</span>
          </div>
        )}
        {onFavorite && (
          <button
            onClick={(e) => { e.preventDefault(); onFavorite(biz); }}
            data-testid={`favorite-btn-${biz.id}`}
            className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center shadow-sm"
            aria-label="Guardar favorito"
          >
            <Heart className={`w-5 h-5 ${isFavorite ? "fill-rose-500 text-rose-500" : "text-slate-700"}`} />
          </button>
        )}
      </Link>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="overline">{biz.category_name || "Negocio"}</span>
        </div>
        <h3 className="font-display text-lg font-semibold leading-tight line-clamp-2 text-slate-900">
          <Link to={`/business/${biz.slug}`} className="hover:underline" data-testid={`business-title-${biz.id}`}>{biz.title}</Link>
        </h3>

        <div className="flex items-center gap-1.5 text-sm text-slate-600">
          <MapPin className="w-3.5 h-3.5" />
          <span>{biz.comuna_name}, {biz.region_name}</span>
        </div>

        {!dense && (
          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
            {biz.years_operating != null && (
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {biz.years_operating} años</span>
            )}
            {biz.monthly_profit != null && (
              <span className="flex items-center gap-1 text-emerald-700"><TrendingUp className="w-3 h-3" /> {fmtCLP(biz.monthly_profit)}/mes</span>
            )}
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {biz.views_count || 0}</span>
            {(biz.interested_count || 0) > 0 && (
              <span className="flex items-center gap-1 text-rose-600 font-semibold" title="Inversionistas interesados">
                <Heart className="w-3 h-3 fill-rose-500" /> {biz.interested_count}
              </span>
            )}
          </div>
        )}

        <div className="mt-auto pt-3 border-t border-zinc-100 flex items-end justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Precio venta</p>
            <p className="font-display text-xl font-bold text-slate-900">{fmtCLP(biz.sale_price)}</p>
          </div>
          <Button size="sm" asChild className="btn-primary" data-testid={`view-business-${biz.id}`}>
            <Link to={`/business/${biz.slug}`}>Ver oportunidad</Link>
          </Button>
        </div>
      </div>
    </article>
  );
}
